import type { ReactNode } from "react";
import { ETIQUETA_ESTADO, gramos, iniciales } from "../lib/formato";
import { useIdioma } from "../lib/idioma";
import { es, type Clave } from "../idiomas/es";

/** Cabecera de página con titulo, descripción y acciones a la derecha. */
export function Cabecera({
  titulo,
  descripcion,
  children,
}: {
  titulo: string;
  descripcion?: string;
  children?: ReactNode;
}) {
  return (
    <div className="cabecera-pagina">
      <div>
        <h1>{titulo}</h1>
        {descripcion && <p>{descripcion}</p>}
      </div>
      {children && <div className="fila ajustable">{children}</div>}
    </div>
  );
}

export function TarjetaDato({
  etiqueta,
  valor,
  pie,
  color,
}: {
  etiqueta: string;
  valor: ReactNode;
  pie?: ReactNode;
  color?: string;
}) {
  return (
    <div className="tarjeta-dato">
      <div className="rotulo">{etiqueta}</div>
      <div className="valor" style={color ? { color } : undefined}>
        {valor}
      </div>
      {pie && <div className="pie-dato">{pie}</div>}
    </div>
  );
}

export function Etiqueta({
  tipo,
  children,
}: {
  tipo?: string;
  children: ReactNode;
}) {
  return <span className={`etiqueta ${tipo ?? ""}`}>{children}</span>;
}

/**
 * El estado del socio, con su color.
 *
 * El texto sale del diccionario y el color de ETIQUETA_ESTADO: el color no
 * cambia con el idioma, y el texto si. Un estado que el diccionario no conozca
 * se enseña tal cual, antes que en blanco.
 */
export function EstadoSocio({ estado }: { estado: string }) {
  const { t } = useIdioma();
  const info = ETIQUETA_ESTADO[estado] ?? { texto: estado, clase: "" };
  const clave = `g.estado.${estado}` as Clave;
  return (
    <Etiqueta tipo={info.clase}>{clave in es ? t(clave) : info.texto}</Etiqueta>
  );
}

export function Aviso({
  tipo = "info",
  children,
}: {
  tipo?: "error" | "ok" | "atencion" | "info";
  children: ReactNode;
}) {
  if (!children) return null;
  return <div className={`aviso ${tipo}`}>{children}</div>;
}

export function Vacio({ children }: { children: ReactNode }) {
  return <div className="vacio">{children}</div>;
}

/**
 * El «Cargando...» de siempre.
 *
 * El texto por defecto sale del diccionario: son cuarenta pantallas las que lo
 * pintan sin decir nada, y cada una pasandolo a mano era una de ellas
 * quedandose en castellano el dia que alguien la olvidara.
 */
export function Cargando({ texto }: { texto?: string }) {
  const { t } = useIdioma();
  return <div className="cargando">{texto ?? t("general.cargando")}</div>;
}

export function Panel({
  titulo,
  acciones,
  children,
  sinCuerpo,
  id,
}: {
  titulo?: ReactNode;
  acciones?: ReactNode;
  children: ReactNode;
  sinCuerpo?: boolean;
  /** Para poder llevar la pantalla hasta este panel desde otro sitio. */
  id?: string;
}) {
  return (
    <section className="panel" id={id}>
      {(titulo || acciones) && (
        <div className="panel-cabecera">
          {typeof titulo === "string" ? <h2>{titulo}</h2> : titulo}
          {acciones && <div className="fila">{acciones}</div>}
        </div>
      )}
      {sinCuerpo ? children : <div className="panel-cuerpo">{children}</div>}
    </section>
  );
}

export function Modal({
  titulo,
  children,
  acciones,
  alCerrar,
}: {
  titulo: string;
  children: ReactNode;
  acciones?: ReactNode;
  alCerrar: () => void;
}) {
  return (
    <div
      className="velo"
      onMouseDown={(e) => e.target === e.currentTarget && alCerrar()}
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
      >
        <header>
          <h2>{titulo}</h2>
          <button
            className="boton fantasma"
            onClick={alCerrar}
            aria-label="Cerrar"
          >
            ✕
          </button>
        </header>
        <div className="cuerpo">{children}</div>
        {acciones && <footer>{acciones}</footer>}
      </div>
    </div>
  );
}

export function Campo({
  etiqueta,
  ayuda,
  children,
}: {
  etiqueta: string;
  ayuda?: string;
  children: ReactNode;
}) {
  return (
    <label className="campo">
      <span>{etiqueta}</span>
      {children}
      {ayuda && <span className="ayuda">{ayuda}</span>}
    </label>
  );
}

/** Barra de consumo frente a un límite, con color según lo gastado. */
export function BarraLimite({
  consumidoMg,
  limiteMg,
  etiqueta,
}: {
  consumidoMg: number;
  limiteMg: number;
  etiqueta: string;
}) {
  const porcentaje =
    limiteMg > 0 ? Math.min(100, (consumidoMg / limiteMg) * 100) : 0;
  const clase = porcentaje >= 100 ? "llena" : porcentaje >= 75 ? "alta" : "";

  return (
    <div>
      <div className="fila separada pequeno" style={{ marginBottom: 4 }}>
        <span className="suave">{etiqueta}</span>
        <span className="tabular">
          {gramos(consumidoMg)} / {gramos(limiteMg)}
        </span>
      </div>
      <div className={`barra ${clase}`}>
        <span style={{ width: `${porcentaje}%` }} />
      </div>
    </div>
  );
}

export function FotoSocio({
  socio,
  grande,
}: {
  socio: { nombre: string; apellidos?: string; fotoUrl?: string | null };
  grande?: boolean;
}) {
  const clase = `foto-socio${grande ? " grande" : ""}`;
  if (socio.fotoUrl)
    return <img className={clase} src={socio.fotoUrl} alt="" />;
  return (
    <div className={clase}>
      {iniciales(socio.nombre, socio.apellidos ?? "")}
    </div>
  );
}

/**
 * Cabecera de tabla que ordena al pulsarla.
 *
 * La flecha solo sale en la columna por la que se esta ordenando: dos flechas
 * a la vez harian dudar de cual manda.
 */
export function ThOrden<C extends string>({
  columna,
  orden,
  descendente,
  alOrdenar,
  num,
  children,
}: {
  columna: C;
  orden: C;
  descendente: boolean;
  alOrdenar: (c: C) => void;
  num?: boolean;
  children: ReactNode;
}) {
  const activa = orden === columna;
  return (
    <th className={num ? "num" : undefined}>
      <button className="th-orden" onClick={() => alOrdenar(columna)}>
        {children}
        <span className="flecha">
          {activa ? (descendente ? "▾" : "▴") : ""}
        </span>
      </button>
    </th>
  );
}
